import React, { useEffect } from 'react';
import { Modal, Card, Row, Col, Button, message, Tag } from 'antd';
import moment from 'moment';
import {
  PauseCircleOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  CloseCircleOutlined,
  GoogleOutlined,
} from '@ant-design/icons';
import PaidSearchReprocessHandler from '../../../../../core/components/paidSearch/paidsearchdetails/hooks/PaidSearchReprocessHandler';
import PaidSearchGoogleOAuthHandler from '../../../../../core/components/paidSearch/paidsearchdetails/hooks/PaidSearchGoogleOAuthHandler';

const AdminModal = ({ visible, setVisible, reprocess, status, updateStatus, orgId, createGoogleOAuth }) => {
  const [currentStatus, setCurrentStatus] = React.useState(status);
  const { reprocessStatus, reprocessData } = PaidSearchReprocessHandler(orgId);
  const { googleOAuthStatus } = PaidSearchGoogleOAuthHandler(orgId);

  useEffect(() => {
    setCurrentStatus(status);
  }, [status]);

  const reprocessProps = () => {
    if (reprocessStatus === 'QUEUED') {
      return {
        color: 'warning',
        text: 'Queued',
        icon: <ClockCircleOutlined />,
      };
    } else if (reprocessStatus === 'PROCESSING') {
      return {
        color: 'processing',
        text: 'Processing',
        icon: <SyncOutlined spin />,
      };
    } else if (reprocessStatus === 'FINISHED') {
      return {
        color: 'success',
        text: 'Processed',
        icon: <CheckCircleOutlined />,
      };
    } else if (reprocessStatus === 'FAILED') {
      return {
        color: 'error',
        text: 'Failed',
        icon: <CloseCircleOutlined />,
      };
    }
  };

  const getLastReprocessTimestamp = () => {
    if (!reprocessData) return null;

    // Show the most recent relevant timestamp based on status
    if (reprocessData.dateFinished) {
      return {
        label: 'Last Completed',
        timestamp: reprocessData.dateFinished,
      };
    } else if (reprocessData.dateProcessed) {
      return {
        label: 'Processing Started',
        timestamp: reprocessData.dateProcessed,
      };
    } else if (reprocessData.dateQueued) {
      return {
        label: 'Last Queued',
        timestamp: reprocessData.dateQueued,
      };
    }
    return null;
  };

  const timestampInfo = getLastReprocessTimestamp();

  return (
    <Modal
      title={`Paid Search Admin Tools`}
      visible={visible}
      onCancel={() => setVisible(false)}
      footer={null}
      width={'900px'}
    >
      <Row type="flex" style={{ justifyContent: 'center' }} gutter={[16, 16]}>
        <Col xs={12} sm={12} md={11}>
          <Card title="Reprocess Paid Search" style={{ height: '100%' }}>
            <Row type="flex" justify="center" align="middle" style={{ marginBottom: '10px' }}>
              {reprocessStatus && (
                <Tag
                  color={reprocessProps().color}
                  icon={reprocessProps().icon}
                  style={{ marginBottom: '15px' }}
                >
                  {reprocessProps().text}
                </Tag>
              )}
            </Row>

            {timestampInfo && (
              <Row type="flex" justify="center" align="middle">
                <div
                  style={{
                    fontSize: '12px',
                    color: '#666',
                    marginBottom: '20px',
                    textAlign: 'center',
                  }}
                >
                  <div>{timestampInfo.label}:</div>
                  <div style={{ fontWeight: '500' }}>
                    {moment(timestampInfo.timestamp).format('MMM D, YYYY h:mm A')}
                  </div>
                </div>
              </Row>
            )}

            <Row type="flex" justify="center" align="middle" style={{ flexDirection: 'column' }}>
              <ReloadOutlined
                style={{ fontSize: '48px', marginBottom: '16px', color: '#1890ff' }}
              />
              <div
                style={{ marginBottom: '16px', fontSize: '14px', color: '#666', fontWeight: '500' }}
              >
                Standard Reprocess
              </div>
              <div
                style={{
                  marginBottom: '16px',
                  fontSize: '12px',
                  color: '#888',
                  textAlign: 'center',
                }}
              >
                This will reprocess the current Paid Search reports
              </div>
              <Button
                icon={<ReloadOutlined />}
                type="primary"
                style={{ width: '100%' }}
                onClick={() => {
                  try {
                    reprocess();
                  } catch (err) {
                    message.error('Failed Reprocessing Paid Search');
                  }
                }}
              >
                Reprocess Paid Search
              </Button>
            </Row>
          </Card>
        </Col>
        <Col xs={12} sm={12} md={11}>
          {currentStatus === 'PAUSED' ? (
            <Card title="Pause Paid Search" style={{ height: '100%' }}>
              <Row type="flex" justify="center" align="middle">
                <PlayCircleOutlined style={{ fontSize: '80px', marginBottom: '20px' }} />
                <div style={{ marginBottom: '20px' }}>
                  This will resume the processing of this Paid Search Reports
                </div>
                <Button
                  icon={<PlayCircleOutlined />}
                  type="primary"
                  onClick={async () => {
                    try {
                      message.loading('Resuming Paid Search');
                      await updateStatus('ACTIVE');

                      message.success('Paid Search Resumed');
                    } catch (err) {
                      message.err('Failed Reprocessing Paid Search');
                    }
                  }}
                >
                  Resume Paid Search
                </Button>
              </Row>
            </Card>
          ) : (
            <Card title="Pause Paid Search" style={{ height: '100%' }}>
              <Row type="flex" justify="center" align="middle">
                <PauseCircleOutlined style={{ fontSize: '80px', marginBottom: '20px' }} />
                <div style={{ marginBottom: '20px' }}>
                  This will stop the processing of this Paid Search Reports
                </div>
                <Button
                  icon={<PauseCircleOutlined />}
                  type="primary"
                  onClick={async () => {
                    try {
                      message.loading('Pausing Paid Search');
                      await updateStatus('PAUSED');

                      message.success('Paid Search Paused');
                    } catch (err) {
                      message.err('Failed Reprocessing Paid Search');
                    }
                  }}
                >
                  Pause Paid Search
                </Button>
              </Row>
            </Card>
          )}
        </Col>
      </Row>
      <Row type="flex" style={{ justifyContent: 'center', marginTop: '16px' }} gutter={[16, 16]}>
        <Col xs={24} sm={24} md={22}>
          <Card title="Google OAuth" style={{ height: '100%' }}>
            <Row type="flex" justify="center" align="middle" style={{ flexDirection: 'column' }}>
              <GoogleOutlined
                style={{ fontSize: '48px', marginBottom: '16px', color: '#4285F4' }}
              />
              <div
                style={{ marginBottom: '16px', fontSize: '14px', color: '#666', fontWeight: '500' }}
              >
                Connect Google Account
              </div>
              <div
                style={{
                  marginBottom: '16px',
                  fontSize: '12px',
                  color: '#888',
                  textAlign: 'center',
                }}
              >
                Authorize Google OAuth to enable Google Ads integration for this organization
              </div>
              <Button
                icon={<GoogleOutlined />}
                type="primary"
                style={{ width: '100%' }}
                onClick={async () => {
                  try {
                    message.loading('Connecting to Google...');
                    await createGoogleOAuth();
                    message.success('Google OAuth created successfully');
                  } catch (err) {
                    message.error('Failed to create Google OAuth');
                  }
                }}
              >
                {googleOAuthStatus && googleOAuthStatus.isValid
                  ? `You are already signed in${googleOAuthStatus.email ? ` as ${googleOAuthStatus.email}` : ''}`
                  : 'Sign in with Google'}
              </Button>
            </Row>
          </Card>
        </Col>
      </Row>
    </Modal>
  );
};

export default AdminModal;
